import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  useGetAllStaffQuery,
  useGetRealEstateTeamsQuery,
} from "../../../store/appApi";
import { selectUser } from "../../../store/authSlice";
import {
  deleteVisitorApi,
  downloadVisitorsWorkbook,
  getVisitorsApi,
  updateVisitorApi,
} from "../../../services/staffReporting";
import {
  formatDateLabel,
  getFormTypeForRole,
  toDateInputValue,
  VISIT_TYPE_OPTIONS,
  VISITOR_TYPE_OPTIONS,
} from "./reportingConfig";
import VisitorRowsEditor, { saveVisitorRows } from "./VisitorRowsEditor";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
// The app shell sets `color-scheme: dark` — pin filter fields to light.
const FILTER_STYLE = {
  backgroundColor: "#fff",
  color: "#111827",
  colorScheme: "light",
};
const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

const TYPE_BADGE = {
  CP: "bg-sky-50 text-sky-700 border-sky-200",
  Customer: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const errorText = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

// Real Estate Sales — the employee's own CP / Customer visitor list. Admins and
// Real Estate team heads pick a member; everyone can download the Excel.
const Visitors = () => {
  const me = useSelector(selectUser);
  const myId = String(me?.localId || "");
  const isAdmin = me?.role === "super-admin";
  const [searchParams] = useSearchParams();

  const { data: staffData } = useGetAllStaffQuery(
    { page: 1, limit: 500 },
    { skip: !isAdmin },
  );
  const { data: reTeamData } = useGetRealEstateTeamsQuery(
    {},
    { skip: isAdmin },
  );

  // Who the picker offers: every active Real Estate Sales staff for an admin;
  // a team head sees himself plus his members; others only themselves.
  const pickable = useMemo(() => {
    if (isAdmin) {
      return (Array.isArray(staffData?.staff) ? staffData.staff : [])
        .filter((s) => s?.status !== false && getFormTypeForRole(s?.role) === 4)
        .map((s) => ({ id: String(s._id), name: s.name, role: s.role }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const byId = new Map();
    if (getFormTypeForRole(me?.role) === 4)
      byId.set(myId, { id: myId, name: `${me?.name || "Me"} (me)`, role: me?.role });
    (reTeamData?.teams || []).forEach((t) =>
      (t.members || []).forEach((m) => {
        if (m && typeof m === "object" && getFormTypeForRole(m.role) === 4)
          byId.set(String(m._id), { id: String(m._id), name: m.name, role: m.role });
      }),
    );
    return Array.from(byId.values());
  }, [isAdmin, staffData, reTeamData, me, myId]);

  const [employeeId, setEmployeeId] = useState(
    searchParams.get("employeeId") || (isAdmin ? "" : myId),
  );
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // New rows typed on this page, saved against `addDate`.
  const [addDate, setAddDate] = useState(toDateInputValue());
  const [newRows, setNewRows] = useState([]);
  const [savingNew, setSavingNew] = useState(false);

  // One row at a time in inline edit.
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const params = useMemo(() => {
    const p = {};
    if (employeeId && employeeId !== myId) p.employeeId = employeeId;
    if (from) p.from = from;
    if (to) p.to = to;
    if (typeFilter) p.visitorType = typeFilter;
    return p;
  }, [employeeId, myId, from, to, typeFilter]);

  const load = useCallback(async () => {
    if (!employeeId) {
      setVisitors([]);
      return;
    }
    setLoading(true);
    try {
      const res = await getVisitorsApi(params);
      setVisitors(Array.isArray(res?.data?.visitors) ? res.data.visitors : []);
    } catch (error) {
      setVisitors([]);
      Swal.fire({
        icon: "error",
        title: "Unable to load",
        text: errorText(error, "Could not load the visitor list."),
      });
    } finally {
      setLoading(false);
    }
  }, [employeeId, params]);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return visitors;
    return visitors.filter(
      (v) =>
        String(v.name || "").toLowerCase().includes(q) ||
        String(v.phone || "").includes(q) ||
        String(v.remarks || "").toLowerCase().includes(q),
    );
  }, [visitors, search]);

  const counts = useMemo(
    () => ({
      cp: shown.filter((v) => v.visitorType === "CP").length,
      customer: shown.filter((v) => v.visitorType === "Customer").length,
    }),
    [shown],
  );

  const canEdit = (v) =>
    isAdmin ||
    String(v.employeeId) === myId ||
    String(v.addedBy || "") === myId;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadVisitorsWorkbook(params);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Download failed",
        text: errorText(error, "Could not download the Excel file."),
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleSaveNew = async () => {
    setSavingNew(true);
    try {
      const saved = await saveVisitorRows({
        employeeId: employeeId !== myId ? employeeId : "",
        date: addDate,
        rows: newRows,
      });
      if (!saved.length) return;
      setNewRows([]);
      await load();
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: `${saved.length} visitor${saved.length === 1 ? "" : "s"} added.`,
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "warning",
        title: "Unable to save",
        text: errorText(error, "Could not save the visitors."),
      });
    } finally {
      setSavingNew(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      await updateVisitorApi(editing._id, {
        date: editing.date,
        visitorType: editing.visitorType,
        visitType: editing.visitType,
        name: editing.name,
        phone: editing.phone,
        remarks: editing.remarks,
      });
      setEditing(null);
      await load();
    } catch (error) {
      Swal.fire({
        icon: "warning",
        title: "Unable to save",
        text: errorText(error, "Could not update this visitor."),
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (v) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete visitor?",
      text: `${v.name} (${v.visitorType}) will be removed from the list.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#e11d48",
    });
    if (!ok.isConfirmed) return;
    try {
      await deleteVisitorApi(v._id);
      await load();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to delete",
        text: errorText(error, "Could not delete this visitor."),
      });
    }
  };

  const showPicker = isAdmin || pickable.length > 1;
  const selectedName =
    pickable.find((p) => p.id === employeeId)?.name || "";

  return (
    <>
      <PageTitle activeMenu="CP / Customer Visitors" motherMenu="Workforce" />
      <div className="space-y-6">
        <section className={`${panelClass} p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Real Estate Sales
              </div>
              <h1 className="mt-3 text-2xl font-semibold text-gray-900">
                CP / Customer Visitors
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Every channel partner and customer who visited, with remarks.
                Download the Excel any time.
              </p>
            </div>
            {showPicker && (
              <div className="w-72">
                <label className={labelClass}>Employee</label>
                <select
                  className={inputClass}
                  value={employeeId}
                  onChange={(e) => {
                    setEmployeeId(e.target.value);
                    setEditing(null);
                    setNewRows([]);
                  }}
                >
                  <option value="">Select a Real Estate Sales staff…</option>
                  {pickable.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.role ? ` · ${p.role}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </section>

        {!employeeId ? (
          <section className={`${panelClass} p-8 text-center text-gray-500`}>
            Pick a Real Estate Sales employee to see their visitors.
          </section>
        ) : (
          <>
            <section className={`${panelClass} p-5 md:p-6`}>
              <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">
                    Add Visitors
                  </h4>
                  <p className="text-xs text-gray-400">
                    Add one row per CP / Customer, then Save Visitors.
                  </p>
                </div>
                <div className="w-44">
                  <label className={labelClass}>Visit Date</label>
                  <input
                    type="date"
                    className={inputClass}
                    style={FILTER_STYLE}
                    value={addDate}
                    max={toDateInputValue()}
                    onChange={(e) => e.target.value && setAddDate(e.target.value)}
                  />
                </div>
              </div>
              <VisitorRowsEditor
                rows={newRows}
                onChange={setNewRows}
                onSave={handleSaveNew}
                saving={savingNew}
              />
            </section>

            <section className={`${panelClass} p-5 md:p-6`}>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-gray-500">
                  {loading
                    ? "Loading…"
                    : `${shown.length} visitor${shown.length === 1 ? "" : "s"} · ${counts.cp} CP · ${counts.customer} Customer`}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="search"
                    className="w-44 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 outline-none focus:border-amber-500"
                    style={FILTER_STYLE}
                    placeholder="Name / phone / remark"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <select
                    className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-900 outline-none focus:border-amber-500"
                    style={FILTER_STYLE}
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    aria-label="Filter by type"
                  >
                    <option value="">CP + Customer</option>
                    {VISITOR_TYPE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt} only
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1">
                    <input
                      type="date"
                      className="w-32 border-0 bg-white text-xs text-gray-900 outline-none"
                      style={FILTER_STYLE}
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      aria-label="From date"
                    />
                    <span className="text-xs text-gray-300">–</span>
                    <input
                      type="date"
                      className="w-32 border-0 bg-white text-xs text-gray-900 outline-none"
                      style={FILTER_STYLE}
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      aria-label="To date"
                    />
                    {(from || to) && (
                      <button
                        type="button"
                        className="text-xs text-gray-400 hover:text-gray-600"
                        title="Clear dates"
                        onClick={() => {
                          setFrom("");
                          setTo("");
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={handleDownload}
                    disabled={downloading || !visitors.length}
                    title={selectedName ? `Download ${selectedName}'s visitors` : "Download Excel"}
                  >
                    {downloading ? "Preparing…" : "Download Excel"}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      {["Date", "Type", "Visit Type", "Name", "Phone", "Remarks", "Added By", ""].map(
                        (h) => (
                          <th key={h} className="px-3 py-2 text-left font-medium">
                            {h}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {!loading && shown.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-3 py-8 text-center text-gray-400">
                          No visitors yet.
                        </td>
                      </tr>
                    )}
                    {shown.map((v) =>
                      editing?._id === v._id ? (
                        <tr key={v._id} className="bg-amber-50/40 align-top">
                          <td className="px-2 py-2">
                            <input
                              type="date"
                              className={inputClass}
                              style={FILTER_STYLE}
                              value={editing.date}
                              onChange={(e) =>
                                setEditing({ ...editing, date: e.target.value })
                              }
                            />
                          </td>
                          <td className="px-2 py-2">
                            <select
                              className={inputClass}
                              value={editing.visitorType}
                              onChange={(e) =>
                                setEditing({ ...editing, visitorType: e.target.value })
                              }
                            >
                              {VISITOR_TYPE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt.toUpperCase()}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            <select
                              className={inputClass}
                              value={editing.visitType}
                              onChange={(e) =>
                                setEditing({ ...editing, visitType: e.target.value })
                              }
                            >
                              <option value="">—</option>
                              {VISIT_TYPE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </td>
                          {["name", "phone", "remarks"].map((key) => (
                            <td key={key} className="px-2 py-2">
                              <input
                                className={inputClass}
                                value={editing[key]}
                                onChange={(e) =>
                                  setEditing({ ...editing, [key]: e.target.value })
                                }
                              />
                            </td>
                          ))}
                          <td className="px-3 py-2 text-gray-500">{v.addedByName || "—"}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-right">
                            <button
                              type="button"
                              className="mr-2 text-xs font-semibold text-emerald-700 hover:underline disabled:opacity-50"
                              onClick={handleSaveEdit}
                              disabled={savingEdit}
                            >
                              {savingEdit ? "Saving…" : "Save"}
                            </button>
                            <button
                              type="button"
                              className="text-xs font-semibold text-gray-500 hover:underline"
                              onClick={() => setEditing(null)}
                            >
                              Cancel
                            </button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={v._id} className="align-top">
                          <td className="whitespace-nowrap px-3 py-2 text-gray-700">
                            {formatDateLabel(v.visitDate)}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase ${TYPE_BADGE[v.visitorType] || ""}`}
                            >
                              {v.visitorType}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-600">{v.visitType || "—"}</td>
                          <td className="px-3 py-2 font-medium text-gray-900">{v.name}</td>
                          <td className="px-3 py-2 text-gray-600">{v.phone || "—"}</td>
                          <td className="max-w-xs px-3 py-2 text-gray-600">{v.remarks || "—"}</td>
                          <td className="px-3 py-2 text-gray-500">{v.addedByName || "—"}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-right">
                            {canEdit(v) && (
                              <>
                                <button
                                  type="button"
                                  className="mr-3 text-xs font-semibold text-sky-700 hover:underline"
                                  onClick={() =>
                                    setEditing({
                                      _id: v._id,
                                      date: String(v.visitDate || "").slice(0, 10),
                                      visitorType: v.visitorType,
                                      visitType: v.visitType || "",
                                      name: v.name || "",
                                      phone: v.phone || "",
                                      remarks: v.remarks || "",
                                    })
                                  }
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="text-xs font-semibold text-rose-600 hover:underline"
                                  onClick={() => handleDelete(v)}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </>
  );
};

export default Visitors;
